import { FaqSection } from './faqTypes';

// What the customer actually receives: the deliverable, the data, and whether
// an AI output is a sellable product. The NDT two-tier rate card is the proof
// that an AI output is already sold as a product line in its own right.

export const PRODUCT_SECTION: FaqSection = {
  id: 'what-we-sell',
  title: 'What the customer is buying',
  intro:
    'This industry buys deliverables, not access. Naming the tangible product on every quote costs nothing and changes how the price is read.',
  items: [
    {
      id: 'tangible-product',
      question: 'Does a user expect a tangible product in return?',
      status: 'proposed',
      answer: [
        '**Yes.** A quote that offers only a subscription will be compared unfavourably against a competitor offering a document, even when the subscription is worth more.',
        'Name the tangible deliverable on every quote:',
      ],
      bullets: [
        '**The compliance record.** Every pipe, joint, weld, inspection, and coating record tied to its source documentation, queryable and exportable.',
        '**The report set.** Material reports per pipe (the model assumes 19 by default), NDT reports, coating records, and fit-up recommendations.',
        '**The handover package** at project close: the dataset in a documented format, plus the archive of source files.',
      ],
      note: '**Recommendation:** quote a named deliverable even on a pure subscription. It costs nothing to state, and it turns an abstract fee into something the customer can point at in a review meeting.',
    },
    {
      id: 'data-as-product',
      question: 'Is data something that is offered as a tangible product?',
      status: 'decided',
      answer: [
        '**The customer\'s raw data is not a CRC Evans product.** What is sold is the work done to it and the structure it comes back in.',
        '**The per-pipe cost model is explicit about this.** The price is built from compute per document, exception handling time, QA overhead, and storage, then grossed up for margin. That is a service. The output, a structured and validated record, is the product.',
        '**Aggregated data is a different question.** The pricing sheet terms already reserve the right to use anonymized and aggregated project data to improve the platform, with no customer-identifiable or project-sensitive content shared without written consent.',
      ],
      note: '**Open point:** selling aggregated data or cross-project benchmarks onward is a genuine future product, but it is not permitted by the current terms. It would need a new agreement and a clear anonymization standard before anyone offers it to a customer.',
    },
    {
      id: 'ai-reports',
      question: 'Are AI generated reports considered a product, and could we sell them?',
      status: 'decided',
      answer: [
        '**Yes, and the model already sells one.** NDT is priced at two tiers: $5 per inspection for Standard and $10 per inspection for NDT with AI, with the $5 AI increment itemized separately. The AI compute cost is roughly $0.40 per inspection, so the AI line runs at about 92 percent margin.',
        '**That is the proof** that an AI output is treated as a product in its own right rather than a feature folded into a base rate.',
        'The rule to apply elsewhere:',
      ],
      bullets: [
        '**Sell the AI output where it replaces human judgment time**, or where it produces a decision the customer would otherwise pay someone to make.',
        '**Price it as a separate line** so the value stays visible.',
        '**Never bundle it as "AI included".** That gives the margin away and makes the price impossible to raise later.',
      ],
    },
  ],
};
